import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { MobileShell } from '../components/MobileShell';
import { TopBar } from '../components/TopBar';
import {
  TrendingUp, Calendar, Star, ChevronRight, ShoppingBag,
  Sparkles, Briefcase, Receipt, User, CalendarDays,
  Ship, Map, Compass, PartyPopper, Car, Hotel, MapPin, Loader2,
} from 'lucide-react';

const CATEGORY_ICONS = {
  yacht: Ship, tour: Map, activity: Compass, party: PartyPopper,
  car_rental: Car, lodging: Hotel,
  yachts: Ship, tours: Map, activities: Compass, nightlife: PartyPopper,
};

export default function ProviderDashboard() {
  const navigate = useNavigate();
  const { user, api } = useAuth();
  const { language } = useLanguage();

  const [stats, setStats] = useState(null);
  const [requests, setRequests] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, reqRes, bookRes] = await Promise.all([
        api().get('/provider/stats'),
        api().get('/provider/requests'),
        api().get('/bookings'),
      ]);
      setStats(statsRes.data);
      setRequests(reqRes.data || []);
      setBookings(bookRes.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const pendingBalance = bookings
    .filter((b) => b.status === 'confirmed' || b.status === 'pending_payment')
    .reduce((sum, b) => sum + (b.provider_balance_due || 0), 0);

  // Count bookings by period (any non-cancelled)
  const startOfDay = (d) => { const x = new Date(d); x.setHours(0,0,0,0); return x; };
  const today = startOfDay(new Date());
  const weekStart = new Date(today); weekStart.setDate(weekStart.getDate() - weekStart.getDay());
  const monthStartDate = new Date(today.getFullYear(), today.getMonth(), 1);

  const bookingRefDate = (b) => {
    const ref = b.date ? new Date(b.date) : new Date(b.created_at);
    return Number.isNaN(ref.getTime()) ? null : startOfDay(ref);
  };
  const activeBookings = bookings.filter((b) => b.status !== 'cancelled');
  const bookingsToday = activeBookings.filter((b) => {
    const d = bookingRefDate(b); return d && d.getTime() === today.getTime();
  }).length;
  const bookingsWeek = activeBookings.filter((b) => {
    const d = bookingRefDate(b); return d && d >= weekStart && d <= today;
  }).length;
  const bookingsMonth = activeBookings.filter((b) => {
    const d = bookingRefDate(b); return d && d >= monthStartDate && d <= today;
  }).length;

  const firstName = user?.name?.split(' ')[0] || '';

  if (loading) {
    return (
      <MobileShell>
        <TopBar showBrand right={<span className="ml-1 text-[10px] bg-rosa text-white px-2 py-0.5 rounded-full font-bold">PRO</span>} />
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-caribbean" /></div>
      </MobileShell>
    );
  }

  return (
    <MobileShell>
      <TopBar
        showBrand
        right={<span className="ml-1 text-[10px] bg-rosa text-white px-2 py-0.5 rounded-full font-bold">PRO</span>}
      />

      <div className="px-4 pt-4" data-testid="provider-dashboard">
        {/* Welcome */}
        <p className="text-xs text-muted-foreground">{language === 'en' ? 'Welcome back' : 'Bienvenido'}</p>
        <h1 className="font-heading text-2xl font-bold leading-tight">
          {language === 'en' ? `Hola, ${firstName}` : `¡Hola, ${firstName}!`}
        </h1>
        <p className="text-sm text-muted-foreground">
          {language === 'en' ? 'Your provider dashboard' : 'Tu panel de proveedor'}
        </p>

        {/* Balance hero — info only, no withdraw */}
        <div className="mt-4 rounded-3xl bg-gradient-to-br from-caribbean to-caribbean/70 p-5 shadow-xl text-white">
          <p className="text-xs opacity-80">
            {language === 'en' ? 'Cash to collect from tourists' : 'Por cobrar a turistas en persona'}
          </p>
          <p className="font-heading text-3xl font-bold mt-1">
            ${pendingBalance.toFixed(2)} <span className="text-sm font-normal opacity-80">USD</span>
          </p>
          <p className="text-[11px] opacity-80 mt-1">
            {language === 'en'
              ? 'BidTrip only charges 17% + IVA as reservation fee. The rest is paid directly to you by the tourist.'
              : 'BidTrip solo cobra 17% + IVA como reserva. El resto te lo paga el turista directamente.'}
          </p>
        </div>

        {/* Bookings stats — by period (clickable) */}
        <div className="grid grid-cols-2 gap-2 mt-4">
          <Link
            to="/bookings?period=today"
            data-testid="stat-today"
            className="bg-card border border-border rounded-2xl p-3 active:scale-95 transition-transform"
          >
            <div className="flex items-center gap-1.5 text-muted-foreground text-[11px] mb-0.5">
              <TrendingUp size={12} /> {language === 'en' ? 'Bookings today' : 'Reservas hoy'}
            </div>
            <p className="font-heading text-2xl font-bold text-caribbean">{bookingsToday}</p>
          </Link>
          <Link
            to="/bookings?period=week"
            data-testid="stat-week"
            className="bg-card border border-border rounded-2xl p-3 active:scale-95 transition-transform"
          >
            <div className="flex items-center gap-1.5 text-muted-foreground text-[11px] mb-0.5">
              <Calendar size={12} /> {language === 'en' ? 'Bookings this week' : 'Reservas semana'}
            </div>
            <p className="font-heading text-2xl font-bold">{bookingsWeek}</p>
          </Link>
          <Link
            to="/bookings?period=month"
            data-testid="stat-month"
            className="bg-card border border-border rounded-2xl p-3 active:scale-95 transition-transform"
          >
            <div className="flex items-center gap-1.5 text-muted-foreground text-[11px] mb-0.5">
              <CalendarDays size={12} /> {language === 'en' ? 'Bookings this month' : 'Reservas mes'}
            </div>
            <p className="font-heading text-2xl font-bold">{bookingsMonth}</p>
          </Link>
          <div className="bg-card border border-border rounded-2xl p-3">
            <div className="flex items-center gap-1.5 text-muted-foreground text-[11px] mb-0.5">
              <Star size={12} className="text-gold fill-gold" /> Rating
            </div>
            <p className="font-heading text-2xl font-bold">{stats?.rating?.toFixed(1) || '0.0'}</p>
            <p className="text-[10px] text-muted-foreground">{stats?.review_count || 0} {language === 'en' ? 'reviews' : 'reseñas'}</p>
          </div>
        </div>

        {/* Quick links */}
        <div className="grid grid-cols-3 gap-2 mt-4">
          <Link to="/provider/catalog" data-testid="provider-link-catalog" className="bg-card border border-border rounded-2xl p-3 flex flex-col items-center gap-1 active:scale-95 transition-transform">
            <ShoppingBag size={20} className="text-caribbean" />
            <span className="text-[11px] font-semibold text-center">{language === 'en' ? 'My Catalog' : 'Mi Catálogo'}</span>
          </Link>
          <Link to="/provider/requests" data-testid="provider-link-requests" className="bg-card border border-border rounded-2xl p-3 flex flex-col items-center gap-1 active:scale-95 transition-transform">
            <Sparkles size={20} className="text-caribbean" />
            <span className="text-[11px] font-semibold text-center">{language === 'en' ? 'Searches' : 'Solicitudes'}</span>
            {requests.length > 0 && <span className="text-[9px] bg-rosa text-white rounded-full px-1.5">{requests.length}</span>}
          </Link>
          <Link to="/bookings" data-testid="provider-link-bookings" className="bg-card border border-border rounded-2xl p-3 flex flex-col items-center gap-1 active:scale-95 transition-transform">
            <Briefcase size={20} className="text-caribbean" />
            <span className="text-[11px] font-semibold text-center">{language === 'en' ? 'Bookings' : 'Reservas'}</span>
            {bookings.length > 0 && <span className="text-[9px] bg-caribbean text-white rounded-full px-1.5">{bookings.length}</span>}
          </Link>
        </div>

        {/* Recent bookings preview */}
        {bookings.length > 0 && (
          <section className="mt-6 pb-4">
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-heading font-bold text-sm">
                {language === 'en' ? 'Recent bookings' : 'Reservas recientes'}
              </h2>
              <Link to="/bookings" className="text-xs text-caribbean font-medium flex items-center gap-0.5">
                {language === 'en' ? 'See all' : 'Ver todas'} <ChevronRight size={12} />
              </Link>
            </div>
            <div className="space-y-2">
              {bookings.slice(0, 3).map((b) => {
                const Icon = CATEGORY_ICONS[b.category] || Compass;
                return (
                  <Link
                    key={b.id}
                    to={`/dashboard/bookings/${b.id}`}
                    data-testid={`recent-booking-${b.id}`}
                    className="block bg-card border border-border rounded-2xl p-3 active:scale-[0.99] transition-transform"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-caribbean/10 flex items-center justify-center shrink-0">
                        <Icon size={18} className="text-caribbean" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm truncate">{b.product_title || b.category}</p>
                        <p className="text-[11px] text-muted-foreground flex items-center gap-1">
                          <MapPin size={10} /> {b.location?.replaceAll('_', ' ')}
                        </p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-[10px] text-muted-foreground">{language === 'en' ? 'Collect' : 'Cobrar'}</p>
                        <p className="font-heading font-bold text-caribbean text-sm">${b.provider_balance_due || b.total_price}</p>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
        )}

        {/* Quick chips */}
        <div className="mt-4 pb-2 flex flex-wrap gap-2">
          <Link to="/profile" className="inline-flex items-center gap-1 px-3 h-8 rounded-full bg-muted text-foreground text-xs">
            <User size={12} /> {language === 'en' ? 'Profile' : 'Perfil'}
          </Link>
          <Link to="/receipts" className="inline-flex items-center gap-1 px-3 h-8 rounded-full bg-muted text-foreground text-xs">
            <Receipt size={12} /> {language === 'en' ? 'Receipts' : 'Recibos'}
          </Link>
        </div>
      </div>
    </MobileShell>
  );
}
